import { motion } from "framer-motion";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle, Calendar, X } from "lucide-react";
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import FloatingADUWizard from "@/components/FloatingADUWizard";
import CanonicalTag from "@/components/CanonicalTag";
import { NeumorphicThemeProvider, useNeumorphicTheme } from "@/contexts/NeumorphicThemeContext";
import NeumorphicThemeToggle from "@/components/NeumorphicThemeToggle";
import { NeumorphicContainer } from "@/components/neumorphic";

const WhoWeAreContent = () => {
  const [showCalendly, setShowCalendly] = useState(false);
  const { theme } = useNeumorphicTheme();
  const isDark = theme === 'dark';
  const textColor = isDark ? 'text-white' : 'text-gray-900';
  const mutedTextColor = isDark ? 'text-gray-300' : 'text-gray-600';
  const cardBg = isDark ? 'bg-[#0a0a0a]' : 'bg-[#f5f5f5]';
  const recessedStyles = isDark 
    ? 'bg-[#0a0a0a] shadow-[inset_8px_8px_16px_#000000,inset_-8px_-8px_16px_#141414]' 
    : 'bg-[#f0f0f0] shadow-[inset_8px_8px_16px_#d8d8d8,inset_-8px_-8px_16px_#ffffff]';

  useEffect(() => {
    // Set page title and meta description
    document.title = "Who We Are | UDA ADU";

    // Add meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Discover the vision, values, and team behind UDA ADU – California\'s most strategic ADU builder, blending Silicon Valley innovation with timeless craftsmanship.');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = 'Discover the vision, values, and team behind UDA ADU – California\'s most strategic ADU builder, blending Silicon Valley innovation with timeless craftsmanship.';
      document.head.appendChild(meta);
    }

    // Add meta keywords
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'About UDA ADU, strategic ADU builder, Silicon Valley ADU team, ADU innovation, who is UDA ADU');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'keywords';
      meta.content = 'About UDA ADU, strategic ADU builder, Silicon Valley ADU team, ADU innovation, who is UDA ADU';
      document.head.appendChild(meta);
    }

    // Add canonical URL
    const canonicalLink = document.createElement('link');
    canonicalLink.rel = 'canonical';
    canonicalLink.href = 'https://www.udaadu.com/who-we-are';
    document.head.appendChild(canonicalLink);

    // Add AboutPage structured data
    const aboutSchema = {
      "@context": "https://schema.org",
      "@type": "AboutPage",
      "name": "Who We Are",
      "description": "Meet the leadership, architects, and strategists behind UDA ADU — California's most strategic design-build ADU firm.",
      "url": "https://www.udaadu.com/who-we-are"
    };
    const aboutScript = document.createElement('script');
    aboutScript.type = 'application/ld+json';
    aboutScript.textContent = JSON.stringify(aboutSchema);
    document.head.appendChild(aboutScript);

    // Cleanup function to remove added elements when component unmounts
    return () => {
      const existingCanonical = document.querySelector('link[rel="canonical"][href="https://www.udaadu.com/who-we-are"]');
      if (existingCanonical) {
        existingCanonical.remove();
      }
      const existingAboutSchema = document.querySelector('script[type="application/ld+json"]');
      if (existingAboutSchema && existingAboutSchema.textContent?.includes('"url": "https://www.udaadu.com/who-we-are"')) {
        existingAboutSchema.remove();
      }
    };
  }, []);

  const openCalendlyModal = () => {
    console.log('Opening Calendly modal');
    setShowCalendly(true);
  };
  const closeCalendlyModal = () => {
    console.log('Closing Calendly modal');
    setShowCalendly(false);
  };
  const processSteps = ["Book a Free Consultation", "Zoom Intro Call — Discuss budget, layout, and goals", "On-Site Meeting — Walk the site with our experts", "Custom ADU Proposal — Transparent, itemized quote", "Agreement Signed — Kickoff begins", "Design + Permits — We handle it all, A to Z", "Permit Approval — We manage all city/county submissions", "Construction Phase — From trench to trim", "Welcome Your ADU — A move-in-ready space, done right"];
  const companyValues = ["Strategic, ROI-driven design-build firm specializing in ADUs", "Bay Area-local team with zoning + permitting mastery", "Financially fluent — we calculate returns before breaking ground", "We believe your land is a balance sheet, not just a yard"];
  
  return (
    <NeumorphicContainer className="min-h-screen">
      <CanonicalTag url="https://www.udaadu.com/who-we-are" />
      <Navigation />
      <NeumorphicThemeToggle />

      {/* Calendly Modal */}
      {showCalendly && <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="relative w-full max-w-2xl h-[70vh] bg-white rounded-lg overflow-hidden shadow-xl">
            <button onClick={closeCalendlyModal} className="absolute top-4 right-4 z-10 p-2 bg-white rounded-full shadow-md hover:bg-gray-100 transition-colors">
              <X className="h-6 w-6 text-gray-600" />
            </button>
            <iframe src="https://calendly.com/udaadu?embed=true&hide_gdpr_banner=1" width="100%" height="100%" frameBorder="0" title="Schedule a meeting" />
          </div>
        </div>}

      {/* Hero Section */}
      <section className="py-12 px-4">
        <div className="max-w-4xl mx-auto">
          <div className={`rounded-2xl p-8 md:p-12 transition-all duration-300 ${recessedStyles}`}>
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6
          }} className="text-center">
              <h1 className="text-4xl text-[#e4683b] mb-6 md:text-4xl font-normal">
                We Don't Build ADUs. We Architect Fortunes.
              </h1>
              <p className={`text-xl md:text-2xl ${mutedTextColor} font-light mb-8`}>
                Welcome to UDA ADU — where Silicon Valley innovation meets timeless craftsmanship.
              </p>
              <p className={`text-lg ${mutedTextColor} leading-relaxed max-w-3xl mx-auto`}>
                At UDA ADU, we believe in smarter space, smarter returns. Our team merges zoning fluency, financial foresight, and architectural precision to build backyard investments that outperform expectations. We aren't contractors. We're architects of ROI.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* New Expertise Section */}
      <section className="px-4 py-[10px]">
        <div className="max-w-4xl mx-auto">
          <div className={`rounded-2xl p-8 md:p-12 transition-all duration-300 ${recessedStyles}`}>
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.1
          }}>
              <h2 className="text-3xl md:text-4xl text-[#e4683b] mb-6 text-center font-normal">
                UDA ADU is built on more than 70 years of combined expertise — spanning construction, design, and executive leadership.
              </h2>
              <div className={`space-y-6 text-lg ${mutedTextColor} leading-relaxed`}>
                <p>
                  Our founding builder brings over 40 years of experience managing large-scale residential and commercial construction projects. He's joined by seasoned general contractors, each with 10+ years in the field, and mentored directly under him. Our lead architect has spent two decades designing residential and multi-use developments across California. The team is guided by a principal with a background in Fortune 500 executive leadership and more than a decade in the construction and real estate space.
                </p>
                <p className="text-xl font-medium text-[#e4683b] text-center">
                  Together, we've created a firm that doesn't just build ADUs — we build strategy, equity, and long-term value.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Team Photo Section */}
      <section className="px-4 py-8">
        <div className="max-w-4xl mx-auto">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }}>
            <div className="rounded-lg overflow-hidden shadow-lg">
              <img 
                src="/lovable-uploads/1d7b21b7-ca8a-4547-9187-d4234401b074.png" 
                alt="UDA ADU construction team working together on site" 
                className="w-full h-auto object-cover"
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Who We Are Section */}
      <section className="py-12 px-4">
        <div className="max-w-4xl mx-auto">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }}>
            <Card className={`mb-12 ${cardBg} border-0`}>
              <CardHeader className="md:p-6 p-4">
                <CardTitle className={`text-2xl text-center mb-2 md:mb-4 ${textColor} md:text-4xl`}>Who We Are</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 md:space-y-6 text-base md:text-lg leading-relaxed md:p-6 p-4 pt-0">
                {companyValues.map((value, index) => <div key={index} className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-[#e4683b] mt-0.5 flex-shrink-0" />
                    <p className={textColor}>{value}</p>
                  </div>)}
              </CardContent>
            </Card>
          </motion.div>

          {/* Quote Box */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.4
        }} className="mb-12">
            <div className={`rounded-2xl p-8 text-center transition-all duration-300 ${recessedStyles}`}>
              <blockquote className="text-2xl md:text-3xl font-light text-[#e4683b] italic">
                "When your property needs a CFO, not just a contractor — that's where we come in."
              </blockquote>
            </div>
          </motion.div>

          {/* Our Process */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.6
        }}>
            <Card className={`${cardBg} border-0`}>
              <CardHeader className="md:p-6 p-4">
                <CardTitle className={`flex items-center gap-2 text-2xl text-center md:text-4xl ${textColor}`}>
                  <Calendar className="h-6 w-6 md:h-8 md:w-8 text-[#e4683b]" />
                  Our Process
                </CardTitle>
              </CardHeader>
              <CardContent className="md:p-6 p-4 pt-0">
                <div className="space-y-4">
                  {processSteps.map((step, index) => <div key={index} className="flex items-start gap-4">
                      <div className={`rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold flex-shrink-0 transition-all duration-300 ${
                        isDark 
                          ? 'bg-[#0a0a0a] text-[#e4683b] shadow-[4px_4px_8px_#000000,-4px_-4px_8px_#141414]' 
                          : 'bg-white text-[#e4683b] shadow-[4px_4px_8px_#d8d8d8,-4px_-4px_8px_#ffffff]'
                      }`}>
                        {index + 1}
                      </div>
                      <div className="flex-1">
                        {index === 0 ? <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                            <span className={`leading-relaxed text-base ${textColor}`}>{step}</span>
                            <button onClick={openCalendlyModal} className={`py-2 text-sm rounded-lg w-fit px-[48px] mx-[23px] font-medium transition-all duration-300 ${
                              isDark
                                ? 'bg-[#0a0a0a] text-[#e4683b] shadow-[6px_6px_12px_#000000,-6px_-6px_12px_#141414] hover:shadow-[8px_8px_16px_#000000,-8px_-8px_16px_#141414]'
                                : 'bg-white text-[#e4683b] shadow-[6px_6px_12px_#d8d8d8,-6px_-6px_12px_#ffffff] hover:shadow-[8px_8px_16px_#d0d0d0,-8px_-8px_16px_#ffffff]'
                            }`}>Book</button>
                          </div> : <span className={`leading-relaxed text-base ${textColor}`}>{step}</span>}
                      </div>
                    </div>)}
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Call to Action */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.8
        }} className="mt-12 text-center">
            <h2 className={`text-2xl md:text-3xl font-bold ${textColor} mb-8`}>
              Meet the team that builds ROI, not just square footage.
            </h2>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link to="/adu-calculator" className={`px-8 py-3 text-lg rounded-xl font-medium transition-all duration-300 ${
                isDark
                  ? 'bg-[#0a0a0a] text-[#e4683b] shadow-[8px_8px_16px_#000000,-8px_-8px_16px_#141414] hover:shadow-[10px_10px_20px_#000000,-10px_-10px_20px_#141414]'
                  : 'bg-white text-[#e4683b] shadow-[8px_8px_16px_#d8d8d8,-8px_-8px_16px_#ffffff] hover:shadow-[10px_10px_20px_#d0d0d0,-10px_-10px_20px_#ffffff]'
              }`}>Get Your ROI Scorecard</Link>
              <Link to="/our-work" className={`px-8 py-3 text-lg rounded-xl font-medium transition-all duration-300 ${
                isDark
                  ? 'bg-[#0a0a0a] text-[#e4683b] shadow-[8px_8px_16px_#000000,-8px_-8px_16px_#141414] hover:shadow-[10px_10px_20px_#000000,-10px_-10px_20px_#141414]'
                  : 'bg-white text-[#e4683b] shadow-[8px_8px_16px_#d8d8d8,-8px_-8px_16px_#ffffff] hover:shadow-[10px_10px_20px_#d0d0d0,-10px_-10px_20px_#ffffff]'
              }`}>View Our Work</Link>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
      <FloatingADUWizard />
    </NeumorphicContainer>
  );
};

const WhoWeAre = () => {
  return (
    <NeumorphicThemeProvider>
      <WhoWeAreContent />
    </NeumorphicThemeProvider>
  );
};

export default WhoWeAre;
